import React, { useState } from 'react';

type ClickableDivProps = {
  onClickOnly?: () => void;
  onTapOnly?: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

const ClickableDiv = ({
  children,
  onClickOnly,
  onTapOnly,
  onMouseDown,
  onMouseUp,
  onTouchStart,
  onTouchEnd,
  ...rest
}: ClickableDivProps) => {
  const [startPosition, setStartPosition] = useState<{ x: number; y: number } | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const movementThreshold = 5; // Maximum movement in pixels to consider it a tap
  const timeThreshold = 300; // Maximum duration in milliseconds to consider it a tap

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Record the starting position of the mouse
    setStartPosition({ x: event.clientX, y: event.clientY });
    onMouseDown?.(event);
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (startPosition) {
      const endPosition = { x: event.clientX, y: event.clientY };
      const distanceMoved =
        Math.abs(endPosition.x - startPosition.x) + Math.abs(endPosition.y - startPosition.y);

      // Check if the movement is within the threshold
      if (distanceMoved < movementThreshold) {
        onClickOnly?.();
      }
    }
    onMouseUp?.(event);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (touch) {
      setStartPosition({ x: touch.clientX, y: touch.clientY });
    }
    setStartTime(Date.now());
    onTouchStart?.(event);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches[0];
    if (startPosition && startTime && touch) {
      const endPosition = {
        x: touch.clientX,
        y: touch.clientY,
      };
      const endTime = Date.now();
      const distanceMoved =
        Math.abs(endPosition.x - startPosition.x) + Math.abs(endPosition.y - startPosition.y);
      const timeElapsed = endTime - startTime;

      // Check if the movement and time are within the thresholds
      if (distanceMoved < movementThreshold && timeElapsed < timeThreshold) {
        onTapOnly?.();
      }
    }
    onTouchEnd?.(event);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      {...rest}
    >
      {children}
    </div>
  );
};

export default ClickableDiv;
